import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CaretLeft, CheckCircle, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/roles";
import {
  acknowledgeIncident,
  adminUpdateIncident,
  getIncident,
  listIncidentAuditEvents,
  resolveIncident,
} from "@/lib/incidents";
import {
  formatIncidentDateTime,
  INCIDENT_TYPE_META,
  INCIDENT_TYPES,
  NOTIFICATION_METHOD_LABELS,
  SEVERITIES,
} from "@/lib/incidentTypes";
import {
  IncidentTypeBadge,
  SeverityBadge,
  StatusBadge,
} from "@/components/incidents/IncidentBadges";
import { getChildById } from "@/lib/dailyReportsDemo";
import {
  deleteIncidentAttachment,
  incidentAttachmentSignedUrl,
  listIncidentAttachments,
  uploadIncidentAttachment,
} from "@/lib/storage";
import { isSupabaseConfigured } from "@/lib/supabase";
import FileUploadCard from "@/components/upload/FileUploadCard";

const TEACHER_LABELS = {
  "demo-teacher": "Ms. Amara Johnson",
  "demo-admin": "Priya Mehta (Admin)",
  "demo-parent": "Rivka Cohen",
};

function personLabel(embedded, fallbackId, demoMap = TEACHER_LABELS) {
  // Live mode returns a PostgREST embedding (object with full_name/email).
  // Demo mode stores just the string id — look it up in the demo map.
  if (embedded && typeof embedded === "object") {
    return embedded.full_name || embedded.email || "Staff member";
  }
  if (demoMap[fallbackId]) return demoMap[fallbackId];
  // If the id is a raw UUID (live mode with RLS hiding the profile row),
  // show a friendly label instead of leaking a UUID into audit text.
  if (fallbackId && /^[0-9a-f-]{36}$/i.test(fallbackId)) return "Staff member";
  return fallbackId || "—";
}

/**
 * Role-aware incident detail view. Shared across parent / teacher / admin.
 * `basePath` drives the "back" link so each shell routes to its own list.
 */
export default function IncidentDetail({ basePath }) {
  const { id } = useParams();
  const { profile, role } = useAuth();
  const navigate = useNavigate();
  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acking, setAcking] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [resolveNote, setResolveNote] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [auditEvents, setAuditEvents] = useState([]);
  const [editing, setEditing] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [edit, setEdit] = useState(null);

  const refreshAudit = async () => {
    try {
      const rows = await listIncidentAuditEvents(id);
      setAuditEvents(rows);
    } catch {
      setAuditEvents([]);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const row = await getIncident(id);
      setIncident(row);
    } catch (err) {
      toast.error(err.message || "Could not load incident");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    if (id && isSupabaseConfigured) refreshAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!id || !isSupabaseConfigured) return;
    (async () => {
      try {
        const rows = await listIncidentAttachments(id);
        setAttachments(rows);
      } catch {
        setAttachments([]);
      }
    })();
  }, [id]);

  const onUploadAttachment = async (file) => {
    const row = await uploadIncidentAttachment({
      incidentId: id,
      file,
      uploadedBy: profile.id,
    });
    setAttachments((prev) => [...prev, row]);
  };

  const onRemoveAttachment = async (item) => {
    await deleteIncidentAttachment({
      id: item.id,
      storagePath: item.storage_path,
    });
    setAttachments((prev) => prev.filter((a) => a.id !== item.id));
  };

  const onAcknowledge = async () => {
    setAcking(true);
    try {
      const row = await acknowledgeIncident({
        incidentId: id,
        profileId: profile.id,
      });
      setIncident(row);
      await refreshAudit();
      toast.success("Acknowledgement recorded");
    } catch (err) {
      toast.error(err.message || "Could not acknowledge");
    } finally {
      setAcking(false);
    }
  };

  const onResolve = async () => {
    setResolving(true);
    try {
      const row = await resolveIncident({
        incidentId: id,
        profileId: profile.id,
        note: resolveNote.trim() || null,
      });
      setIncident(row);
      await refreshAudit();
      toast.success("Incident marked resolved");
    } catch (err) {
      toast.error(err.message || "Could not resolve");
    } finally {
      setResolving(false);
    }
  };

  const startEdit = () => {
    if (!incident) return;
    setEdit({
      incident_type: incident.incident_type,
      severity: incident.severity,
      occurred_at: incident.occurred_at
        ? new Date(incident.occurred_at).toISOString().slice(0, 16)
        : "",
      location: incident.location || "",
      description: incident.description || "",
      action_taken: incident.action_taken || "",
      resolution_note: incident.resolution_note || "",
    });
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEdit(null);
  };

  const buildPatch = () => {
    if (!edit) return {};
    const patch = {};
    const ackLocked = !!incident.acknowledged_at;
    if (!ackLocked) {
      if (edit.incident_type !== incident.incident_type) patch.incident_type = edit.incident_type;
      if (edit.severity !== incident.severity) patch.severity = edit.severity;
      const newOccurred = edit.occurred_at ? new Date(edit.occurred_at).toISOString() : null;
      if (newOccurred && newOccurred !== incident.occurred_at) patch.occurred_at = newOccurred;
      if ((edit.location || null) !== (incident.location || null)) patch.location = edit.location || "";
      if (edit.description.trim() !== (incident.description || "")) patch.description = edit.description.trim();
      if (edit.action_taken.trim() !== (incident.action_taken || "")) patch.action_taken = edit.action_taken.trim();
    }
    if ((edit.resolution_note || "") !== (incident.resolution_note || "")) {
      patch.resolution_note = edit.resolution_note || "";
    }
    return patch;
  };

  const onSaveEdit = async () => {
    const patch = buildPatch();
    if (Object.keys(patch).length === 0) {
      cancelEdit();
      return;
    }
    setSavingEdit(true);
    try {
      const row = await adminUpdateIncident({ incidentId: id, patch });
      setIncident(row);
      await refreshAudit();
      toast.success("Incident updated");
      cancelEdit();
    } catch (err) {
      toast.error(err.message || "Could not save changes");
    } finally {
      setSavingEdit(false);
    }
  };

  if (loading) {
    return (
      <div
        data-testid="incident-detail-loading"
        className="flex items-center justify-center py-16"
      >
        <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  if (!incident) {
    return (
      <div className="py-16 text-center text-sm text-neutral-500">
        Incident not found.
      </div>
    );
  }

  const child =
    incident.child ||
    getChildById(incident.child_id) ||
    null;
  const childName = child
    ? `${child.first_name} ${child.last_name || ""}`.trim()
    : "—";
  const classroomName = incident.classroom?.name || "—";
  const typeMeta = INCIDENT_TYPE_META[incident.incident_type] || INCIDENT_TYPE_META.other;

  const canAcknowledge =
    role === ROLES.PARENT && !incident.acknowledged_at;
  const canResolve =
    (role === ROLES.ADMIN || role === ROLES.TEACHER) &&
    incident.status !== "resolved" &&
    incident.status !== "void";

  const isAdmin = role === ROLES.ADMIN;
  const ackLocked = !!incident.acknowledged_at;
  const resolved = incident.status === "resolved";
  const canAdminEdit = isAdmin && !resolved;

  return (
    <div
      data-testid="incident-detail"
      data-incident-status={incident.status}
      className="space-y-5"
    >
      <Link
        to={basePath}
        data-testid="incident-detail-back"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900"
      >
        <CaretLeft size={16} weight="bold" />
        Back
      </Link>

      <header className="rounded-2xl border border-neutral-200 bg-white p-5 sm:p-6">
        <div className="flex flex-wrap items-start gap-3">
          <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${typeMeta.bg}`}>
            <typeMeta.icon size={22} weight="duotone" className={typeMeta.color} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
              Incident report
            </p>
            <h1 className="mt-0.5 font-heading text-2xl font-semibold tracking-tight text-neutral-900 sm:text-3xl">
              {childName}
            </h1>
            <p className="mt-1 text-sm text-neutral-600">
              {formatIncidentDateTime(incident.occurred_at)}
              {incident.location ? ` · ${incident.location}` : ""}
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <IncidentTypeBadge type={incident.incident_type} />
          <SeverityBadge severity={incident.severity} />
          <StatusBadge status={incident.status} />
          {canAdminEdit && !editing && (
            <button
              type="button"
              data-testid="incident-admin-edit"
              onClick={startEdit}
              className="ml-auto inline-flex items-center gap-1 rounded-full border border-primary/40 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-primary-hover hover:bg-primary/5"
            >
              {ackLocked ? "Edit resolution note" : "Edit"}
            </button>
          )}
        </div>
        {ackLocked && canAdminEdit && (
          <p
            data-testid="incident-edit-lock-notice"
            className="mt-3 rounded-xl border border-amber-300 bg-amber-50/60 px-3 py-2 text-[12px] text-amber-900"
          >
            Parent has acknowledged this report. Core fields are locked — only
            the resolution note can still be edited.
          </p>
        )}
      </header>

      {editing && canAdminEdit && (
        <section
          data-testid="incident-admin-edit-form"
          className="space-y-3 rounded-2xl border border-primary/30 bg-primary/5 p-5"
        >
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-primary-hover">
            Editing incident
          </p>
          {!ackLocked && (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Type</span>
                  <select
                    data-testid="incident-edit-type"
                    value={edit.incident_type}
                    onChange={(e) => setEdit((s) => ({ ...s, incident_type: e.target.value }))}
                    className={EDIT_INPUT}
                  >
                    {INCIDENT_TYPES.map((t) => (
                      <option key={t} value={t}>{INCIDENT_TYPE_META[t]?.label || t}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Severity</span>
                  <select
                    data-testid="incident-edit-severity"
                    value={edit.severity}
                    onChange={(e) => setEdit((s) => ({ ...s, severity: e.target.value }))}
                    className={EDIT_INPUT}
                  >
                    {SEVERITIES.map((s) => (
                      <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Occurred at</span>
                  <input
                    type="datetime-local"
                    data-testid="incident-edit-occurred-at"
                    value={edit.occurred_at}
                    onChange={(e) => setEdit((s) => ({ ...s, occurred_at: e.target.value }))}
                    className={EDIT_INPUT}
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Location</span>
                  <input
                    type="text"
                    data-testid="incident-edit-location"
                    value={edit.location}
                    onChange={(e) => setEdit((s) => ({ ...s, location: e.target.value }))}
                    className={EDIT_INPUT}
                  />
                </label>
              </div>
              <label className="block">
                <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Description</span>
                <textarea
                  rows={3}
                  data-testid="incident-edit-description"
                  value={edit.description}
                  onChange={(e) => setEdit((s) => ({ ...s, description: e.target.value }))}
                  className={EDIT_INPUT}
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Action taken</span>
                <textarea
                  rows={2}
                  data-testid="incident-edit-action-taken"
                  value={edit.action_taken}
                  onChange={(e) => setEdit((s) => ({ ...s, action_taken: e.target.value }))}
                  className={EDIT_INPUT}
                />
              </label>
            </>
          )}
          <label className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">
              Resolution note {ackLocked ? "(only editable field)" : "(optional)"}
            </span>
            <textarea
              rows={2}
              data-testid="incident-edit-resolution-note"
              value={edit.resolution_note}
              onChange={(e) => setEdit((s) => ({ ...s, resolution_note: e.target.value }))}
              className={EDIT_INPUT}
            />
          </label>
          <div className="flex flex-wrap justify-end gap-2 pt-1">
            <button
              type="button"
              data-testid="incident-edit-cancel"
              onClick={cancelEdit}
              className="rounded-full px-4 py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-100"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="incident-edit-save"
              onClick={onSaveEdit}
              disabled={savingEdit}
              className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60"
            >
              {savingEdit ? "Saving…" : "Save changes"}
            </button>
          </div>
        </section>
      )}

      <Section title="Description">
        <p data-testid="incident-description-text" className="whitespace-pre-wrap text-sm leading-relaxed text-neutral-800">
          {incident.description}
        </p>
      </Section>

      <Section title="Action taken">
        <p data-testid="incident-action-text" className="whitespace-pre-wrap text-sm leading-relaxed text-neutral-800">
          {incident.action_taken}
        </p>
      </Section>

      <Section title="Audit">
        <dl className="grid grid-cols-1 gap-y-2 text-sm sm:grid-cols-2">
          <Row label="Classroom" value={classroomName} />
          <Row
            label="Reported by"
            value={personLabel(incident.reporter, incident.reported_by)}
          />
          <Row label="Filed" value={formatIncidentDateTime(incident.created_at)} />
          <Row
            label="Parent notified"
            value={
              incident.parent_notified_at
                ? `${NOTIFICATION_METHOD_LABELS[incident.parent_notification_method] || "—"} · ${formatIncidentDateTime(incident.parent_notified_at)}`
                : "Not yet"
            }
            testId="incident-parent-notified-line"
          />
          <Row
            label="Acknowledged"
            value={
              incident.acknowledged_at
                ? `${personLabel(incident.acknowledger, incident.acknowledged_by)} · ${formatIncidentDateTime(incident.acknowledged_at)}`
                : "Pending family acknowledgement"
            }
            testId="incident-ack-line"
          />
          {incident.resolved_at && (
            <Row
              label="Resolved"
              value={`${personLabel(incident.resolver, incident.resolved_by)} · ${formatIncidentDateTime(incident.resolved_at)}`}
              testId="incident-resolved-line"
            />
          )}
        </dl>
        {incident.resolution_note && (
          <p className="mt-3 rounded-xl bg-neutral-50 px-3 py-2 text-sm text-neutral-700">
            <span className="font-semibold">Resolution note · </span>
            {incident.resolution_note}
          </p>
        )}
      </Section>

      {/* Parent acknowledge */}
      {canAcknowledge && (
        <section
          data-testid="incident-acknowledge-section"
          className="rounded-2xl border border-primary/30 bg-primary/5 p-5"
        >
          <div className="flex items-start gap-3">
            <ShieldCheck
              size={22}
              weight="duotone"
              className="mt-0.5 text-primary-hover"
            />
            <div className="min-w-0 flex-1">
              <p className="font-heading text-base font-semibold text-neutral-900">
                Acknowledge this report
              </p>
              <p className="mt-1 text-sm text-neutral-700">
                Confirming acknowledgement only records that you have read the
                report — it does not waive any rights.
              </p>
            </div>
          </div>
          <button
            type="button"
            data-testid="incident-acknowledge-button"
            onClick={onAcknowledge}
            disabled={acking}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <CheckCircle size={16} weight="fill" />
            {acking ? "Recording…" : "I acknowledge receipt"}
          </button>
        </section>
      )}

      {/* Admin/teacher resolve */}
      {canResolve && (
        <section
          data-testid="incident-resolve-section"
          className="rounded-2xl border border-neutral-200 bg-white p-5"
        >
          <p className="font-heading text-base font-semibold text-neutral-900">
            Resolve incident
          </p>
          <p className="mt-1 text-sm text-neutral-600">
            Close this report once any required follow-up is complete.
          </p>
          <textarea
            data-testid="incident-resolve-note"
            rows={2}
            value={resolveNote}
            onChange={(e) => setResolveNote(e.target.value)}
            placeholder="Resolution note (optional)"
            className="mt-3 w-full rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-sm text-neutral-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          <button
            type="button"
            data-testid="incident-resolve-button"
            onClick={onResolve}
            disabled={resolving}
            className="mt-3 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {resolving ? "Saving…" : "Mark resolved"}
          </button>
        </section>
      )}

      {/* Navigation hint for teacher who just created an incident */}
      {role === ROLES.TEACHER && (
        <button
          type="button"
          onClick={() => navigate("/teacher/incidents")}
          className="text-sm font-medium text-primary-hover hover:underline"
          data-testid="incident-back-to-list"
        >
          Back to incident list
        </button>
      )}

      {(role === ROLES.ADMIN || role === ROLES.TEACHER) && (
        <Section title="Audit trail">
          {auditEvents.length === 0 ? (
            <p
              data-testid="incident-audit-trail"
              data-empty="true"
              className="text-sm text-neutral-500"
            >
              No history yet — the first edit will appear here.
            </p>
          ) : (
            <ol data-testid="incident-audit-trail" className="space-y-3">
              {auditEvents.map((ev) => (
                <AuditEntry
                  key={ev.id}
                  ev={ev}
                  actorLabel={personLabel(ev.actor, ev.actor_profile_id)}
                />
              ))}
            </ol>
          )}
        </Section>
      )}

      {isSupabaseConfigured && (
        <FileUploadCard
          testId="incident-attachments"
          label="Attachments"
          emptyLabel={
            role === ROLES.PARENT
              ? "No photos or documents were attached to this report."
              : "Upload photos or documents (e.g. incident photos, first-aid forms)."
          }
          accept="image/*,.pdf,.doc,.docx"
          mode="file"
          items={attachments}
          canEdit={role === ROLES.ADMIN || role === ROLES.TEACHER}
          onUpload={onUploadAttachment}
          onRemove={onRemoveAttachment}
          resolveSignedUrl={incidentAttachmentSignedUrl}
          maxSizeMb={10}
        />
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-2xl border border-neutral-200 bg-white p-5">
      <p className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
        {title}
      </p>
      {children}
    </section>
  );
}

function Row({ label, value, testId }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="w-36 flex-shrink-0 text-xs font-bold uppercase tracking-[0.12em] text-neutral-500">
        {label}
      </dt>
      <dd
        data-testid={testId}
        className="text-sm text-neutral-800"
      >
        {value}
      </dd>
    </div>
  );
}

const EDIT_INPUT =
  "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";

const FIELD_LABELS = {
  incident_type: "Type",
  severity: "Severity",
  occurred_at: "Occurred at",
  location: "Location",
  description: "Description",
  action_taken: "Action taken",
  parent_notified_at: "Parent notified at",
  parent_notification_method: "Parent notification method",
  classroom_id: "Classroom",
  child_id: "Child",
  resolution_note: "Resolution note",
  status: "Status",
};

const ACTION_LABELS = {
  created: "Created the report",
  updated: "Updated the report",
  resolved: "Marked as resolved",
};

function shorten(value) {
  if (value == null || value === "") return "—";
  const str = String(value);
  return str.length > 60 ? `${str.slice(0, 57)}…` : str;
}

function AuditEntry({ ev, actorLabel }) {
  const changes = Array.isArray(ev.changes) ? ev.changes : [];
  return (
    <li
      data-testid={`incident-audit-${ev.id}`}
      data-action={ev.action}
      className="rounded-2xl border border-neutral-200 bg-white p-3"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-semibold text-neutral-900">
          {ACTION_LABELS[ev.action] || ev.action}
        </p>
        <p className="text-[11px] text-neutral-500">
          {actorLabel} · {formatIncidentDateTime(ev.created_at)}
        </p>
      </div>
      {changes.length > 0 && (
        <ul className="mt-2 space-y-1 text-[12px] text-neutral-700">
          {changes.map((c, i) => (
            <li key={i} className="flex flex-wrap gap-1">
              <span className="font-semibold text-neutral-600">
                {FIELD_LABELS[c.field] || c.field}:
              </span>
              <span className="text-neutral-500 line-through">{shorten(c.before)}</span>
              <span aria-hidden>→</span>
              <span className="text-neutral-900">{shorten(c.after)}</span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
